import { Table } from 'antd';
import React, { useMemo } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { DAYTIME_FORMAT } from '../../constants';
import useColumnConfig from '../../hooks/useColumnConfig';
import ColumnConfigDrawer from '../ColumnConfigDrawer';

const FIXED_KEYS = ['name', 'action'];

export default function FleetsTable({ withAction, dataSource, selectedRowKeys, onSelectChange, configRef, ...props }) {
  const { t } = useTranslation('fleet');
  const { t: tc } = useTranslation('common');
  const allColumns = useMemo(() => {
    const cols = [
      {
        title: t('name'),
        dataIndex: 'name',
        key: 'name',
        fixed: 'left',
        className: 'border-right',
        width: 200,
      },
      {
        title: t('tag'),
        dataIndex: 'tag',
        key: 'tag',
        width: 70,
        render: (value) => {
          return <span className={'status ' + value.toLowerCase()}>{tc(`fleetTags.${value}`)}</span>;
        },
      },
      {
        title: t('hub'),
        key: 'hubIndex',
        width: 200,
        render: (record) => {
          return record.hub?.name || '';
        },
      },
      {
        title: t('accessCode'),
        dataIndex: 'accessCode',
        key: 'accessCode',
      },
      {
        title: t('weight'),
        dataIndex: 'weight',
        key: 'weight',
        render: (value) => {
          if (value) {
            return `${value} kg`;
          }
        },
      },
      {
        title: t('volume'),
        dataIndex: 'volume',
        key: 'volume',
        render: (value) => {
          if (value) {
            return `${value} m3`;
          }
        },
      },
      {
        title: tc('updatedAt'),
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        width: 250,
        render: (value) => {
          return value ? moment(value).format(DAYTIME_FORMAT) : '';
        },
      },
      {
        title: tc('status'),
        dataIndex: 'status',
        key: 'status',
        fixed: 'right',
        width: 200,
        className: 'border-left',
        render: (value) => {
          return <span className={'status fleet ' + value.toLowerCase()}>{tc(`fleetStatuses.${value}`)}</span>;
        },
      },
    ];

    if (withAction) {
      cols.push({
        key: 'action',
        width: 120,
        className: 'border-left',
        fixed: 'right',
        render(record) {
          return withAction(record);
        },
      });
    }

    return cols;
  }, [withAction]);

  const columnConfig = useColumnConfig('fleets-columns', allColumns, FIXED_KEYS);

  // Expose openDrawer to parent via ref
  if (configRef) {
    configRef.current = { openDrawer: columnConfig.openDrawer, pageSize: columnConfig.pageSize };
  }

  const rowSelection =
    selectedRowKeys && onSelectChange
      ? {
          selectedRowKeys,
          onChange: onSelectChange,
        }
      : undefined;

  return (
    <div className="table-job-orders table-common">
      <Table
        columns={columnConfig.visibleColumns}
        rowSelection={rowSelection}
        dataSource={dataSource}
        rowClassName={(record, index) => (index % 2 === 0 ? 'table-row-dark' : 'table-row-light')}
        {...props}
      />
      <ColumnConfigDrawer
        open={columnConfig.drawerOpen}
        onClose={columnConfig.closeDrawer}
        columns={columnConfig.orderedAllColumns}
        hidden={columnConfig.hidden}
        fixedKeys={FIXED_KEYS}
        onToggle={columnConfig.toggleColumn}
        onMove={columnConfig.moveColumn}
        onReset={columnConfig.resetConfig}
        pageSize={columnConfig.pageSize}
        onPageSizeChange={columnConfig.setPageSize}
      />
    </div>
  );
}
